import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AlertCircle, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { DeleteConfirmationDialog } from "./DeleteConfirmationDialog";
import { SecretRow } from "./SecretRow";
import { secretSelectors } from "./secretReducer";
import type { Secret } from "./types";
import { useInputRefs } from "./useInputRefs";
import { useSecretState } from "./useSecretState";

interface EnvVarPopoverProps {
  projectId: string;
}

export function EnvVarPopover({ projectId }: EnvVarPopoverProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState<{
    index: number;
    key: string;
  } | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { setInputRef, focusInput } = useInputRefs();

  const {
    state,
    isLoadingSecrets,
    secretsError,
    saveSecret,
    deleteSecret,
    addNewSecret,
    removeSecret,
    updateSecret,
    startEditing,
    stopEditing,
    toggleVisibility,
    canSaveSecret,
    isOperationInProgress,
  } = useSecretState({ projectId });

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const secret = secretSelectors.getSecretByIndex(state, index);
    if (!secret) {
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      // Save both new and existing secrets on Enter
      if (canSaveSecret(index)) {
        saveSecret(index);
      }
    }

    if (e.key === "Escape") {
      e.preventDefault();
      const isNewSecret = !secret.isExisting;
      if (isNewSecret && !secret.key && !secret.value) {
        removeSecret(index);
      } else {
        stopEditing(secret.key);
      }
    }
  };

  const handleBlur = (index: number) => {
    const secret = secretSelectors.getSecretByIndex(state, index);
    if (!secret) {
      return;
    }

    const isSecretEditing = secretSelectors.isEditing(state, secret.key);
    const isNewSecret = !secret.isExisting;

    if (isNewSecret) {
      // For new secrets, only save if both key and value are provided
      if (secret.key && secret.value && canSaveSecret(index)) {
        saveSecret(index);
      } else if (!secret.key && !secret.value) {
        // Remove completely empty new secrets
        removeSecret(index);
      }
      // If partially filled, just leave it as is for the user to complete
    } else if (isSecretEditing && canSaveSecret(index)) {
      // For existing secrets being edited, save on blur
      saveSecret(index);
    } else {
      stopEditing(secret.key);
    }
  };

  const handlePopoverOpenChange = (open: boolean) => {
    // Prevent closing the popover when delete dialog is open
    if (!open && deleteDialogOpen) {
      return;
    }

    setIsPopoverOpen(open);

    // Clean up partially filled secrets when closing
    if (!open) {
      // Remove invalid secrets - collect indices first to avoid array modification during iteration
      const indicesToRemove: number[] = [];

      state.secrets.forEach((secret, index) => {
        const isNewSecret = !secret.isExisting;
        if (
          isNewSecret &&
          ((!secret?.key && !secret?.value) || // Completely empty secret
            (secret?.key && !secret?.value) || // Secret with key but no value
            (secret?.value && !secret?.key)) // Secret with value but no key
        ) {
          indicesToRemove.push(index);
        }
      });

      // Remove secrets in reverse order to maintain correct indices
      for (let i = indicesToRemove.length - 1; i >= 0; i--) {
        removeSecret(indicesToRemove[i]);
      }

      // Clear UI states
      for (const key of state.editing) {
        stopEditing(key);
      }
    }
  };

  const handleStartEditing = (secret: Secret, index: number) => {
    startEditing(secret.key);
    updateSecret(index, { value: "" });
    setTimeout(() => {
      focusInput(index, "value");
    }, 0);
  };

  const handleAddNewSecret = () => {
    addNewSecret();
    // Focus will be handled by the useSecretState hook
  };

  const handleDeleteClick = (index: number, secret: Secret) => {
    if (!secret.isExisting) {
      // For new secrets, just remove from array
      removeSecret(index);
    } else {
      // For existing secrets, show confirmation dialog
      setSecretToDelete({
        index,
        key: secret.key,
      });
      setDeleteDialogOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (secretToDelete) {
      await deleteSecret(secretToDelete.index, secretToDelete.key);
      setDeleteDialogOpen(false);
      setSecretToDelete(null);
    }
  };

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            size="icon"
            className="h-7 w-7 relative"
            aria-label="Add Environment Variable"
          >
            <KeyRound className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[396px] p-0 border-0"
          align="end"
          side="bottom"
        >
          <div className="">
            <div className="px-1">
              <div className="h-8 p-2 font-4 leading-5 flex items-center justify-between">
                <h4>Add Environment Variables</h4>
              </div>
            </div>
            <hr className="my-1" />
            <div className="py-3">
              <div className="flex flex-col gap-4 px-3">
                <p className="text-xs leading-4">
                  Environment variables will be encrypted and stored securely on
                  our servers.
                </p>

                {/* Error display */}
                {Boolean(secretsError) && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="text-xs">
                      {secretsError instanceof Error
                        ? secretsError.message
                        : String(secretsError) || "Failed to load secrets"}
                    </span>
                  </div>
                )}

                {isLoadingSecrets ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Loading secrets...
                    </span>
                  </div>
                ) : (
                  <>
                    {state.secrets.map((secret, index) => (
                      <SecretRow
                        key={secret.id || `fallback-${index}`}
                        secret={secret}
                        index={index}
                        state={state}
                        onUpdateSecret={updateSecret}
                        onKeyDown={handleKeyDown}
                        onStartEditing={handleStartEditing}
                        onBlur={handleBlur}
                        onToggleVisibility={toggleVisibility}
                        onDeleteClick={handleDeleteClick}
                        setInputRef={setInputRef}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>

            <div className="pt-0 p-3">
              <Button
                type="button"
                onClick={handleAddNewSecret}
                variant="secondary"
                className="inline-flex h-7 py-0 px-3"
                disabled={isLoadingSecrets || isOperationInProgress}
              >
                + Add Variable
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Portal the delete dialog outside the popover to prevent interaction conflicts */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        secretKey={secretToDelete?.key}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setSecretToDelete(null);
          }
        }}
        onConfirm={confirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSecretToDelete(null);
        }}
      />
    </>
  );
}
